import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useLocale } from '@hooks/useLocale';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation, A11y } from 'swiper/modules';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';
import Heading from '@components/ui/Heading';
import IconWrapper from '@components/ui/Icons';
import ProductCardAlt from './ProductCardAlt';
import useObjectVisibility from '@hooks/useObjectVisibility';
import { PdpCollection } from '@adapters/bigcommerce/types/product';
import {
  GA4_EVENT_NAMES,
  trackProductEvent,
  generateProductTrackingData,
} from '@utils/gtm/ga4';
import { setSelectItemCookie } from '@utils/gtm';
import { getVisibleSlideIndexes } from '@utils/swiper';

import 'swiper/css';

import {
  Loader,
  AlgonomyProduct,
  AlgonomyPlacement,
} from '@components/global/Recommendations/Loader';

import styles from './ProductCollectionComponent.module.scss';

export { Loader as RecommendationsLoader };
export type { AlgonomyPlacement };

export interface RecommendationsProps {
  placement: PdpCollectionData;
}

interface PdpCollectionData {
  description?: PdpCollection['description'];
  items?: AlgonomyPlacement['items'] | [];
  linkText?: PdpCollection['linkText'];
  message?: AlgonomyPlacement['message'];
  name?: AlgonomyPlacement['name'];
  strategy?: AlgonomyPlacement['strategy'];
  url?: PdpCollection['url'];
}

const { SELECT_ITEM, VIEW_ITEM_LIST } = GA4_EVENT_NAMES;

const ProductCollectionComponent = ({ placement }: RecommendationsProps) => {
  const { t } = useTranslation('pdp');
  const locale = useLocale();
  const [recommendationSwiper, setRecommendationSwiper] =
    useState<SwiperType | null>(null);

  const [fadeLeft, setFadeLeft] = useState(false);
  const [fadeRight, setFadeRight] = useState(false);
  const [viewedSlides, setViewedSlides] = useState<number[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const setFade = (swiper: SwiperType) => {
    setFadeLeft(!swiper.isBeginning);
    setFadeRight(!swiper.isEnd);
  };

  useEffect(() => {
    if (placement?.items?.length && recommendationSwiper) {
      setFade(recommendationSwiper);
    }
  }, [placement?.items?.length, recommendationSwiper]);

  const generateTrackingData = useCallback(
    (i: number) => {
      if (!placement?.items?.[i]) {
        return null;
      }
      return generateProductTrackingData(
        placement.items[i],
        i,
        placement?.name
      );
    },
    [placement.items, placement?.name]
  );

  const trackClick = (i: number) => {
    const product = generateTrackingData(i);
    if (product) {
      setSelectItemCookie({
        eventName: SELECT_ITEM,
        products: [product],
        itemListName: placement?.name || '',
      });
    }
  };

  const trackSlideView = useCallback(
    (visibleSlideIndexes: number[] = []) => {
      const untrackedSlideIndexes = visibleSlideIndexes.filter(
        (slide) => viewedSlides?.indexOf(slide) === -1
      );
      if (untrackedSlideIndexes.length > 0) {
        trackProductEvent({
          eventName: VIEW_ITEM_LIST,
          products: untrackedSlideIndexes.map((i) => generateTrackingData(i)),
          itemListName: placement.name,
        });

        setViewedSlides((currentSlides) => [
          ...currentSlides,
          ...untrackedSlideIndexes,
        ]);
      }
    },
    [generateTrackingData, placement?.name, viewedSlides]
  );
  const [hasInitialTrackingFired, setInitialTrackingFired] = useState(false);
  const isCarouselVisible = useObjectVisibility(containerRef);
  useEffect(() => {
    if (recommendationSwiper && isCarouselVisible && !hasInitialTrackingFired) {
      trackSlideView(getVisibleSlideIndexes(recommendationSwiper));
      setInitialTrackingFired(true);
    }
  }, [
    isCarouselVisible,
    recommendationSwiper,
    trackSlideView,
    hasInitialTrackingFired,
  ]);

  const slides = placement?.items?.map(
    (product: AlgonomyProduct, i: number) => (
      <SwiperSlide key={product.id}>
        <ProductCardAlt
          displayName={product.displayName}
          displayPrice={product.displayPrice}
          productImage={product.imageURL}
          productUrl={product?.linkURL ?? product?.detailURL}
          salePrice={product.displaySalePrice}
          trackClick={() => trackClick(i)}
        />
      </SwiperSlide>
    )
  );

  return (
    <section className={styles.container}>
      <div className={styles.header}>
        <Heading semanticLevel="h2" styleLevel="2" className={styles.heading}>
          {placement?.message}
        </Heading>
        <span className={styles.subHeading}>{placement?.description}</span>
      </div>
      <Swiper
        modules={[Navigation, A11y]}
        className={cn(styles.swiper, {
          [styles.atEnd]: fadeRight,
        })}
        navigation={{
          nextEl: styles.swipeButtNext,
          prevEl: styles.swipeButtPrev,
        }}
        slidesPerView={1.5}
        spaceBetween={16}
        initialSlide={0}
        breakpoints={{
          820: {
            slidesPerView: 3.25,
            slidesPerGroup: 3,
          },
        }}
        onSwiper={setRecommendationSwiper}
        onReachBeginning={setFade}
        onReachEnd={setFade}
        watchSlidesProgress
        threshold={2}
        onSlideChange={(swiper) => {
          trackSlideView(getVisibleSlideIndexes(swiper));
          setFade(swiper);
        }}
        onInit={(swiper) => {
          setFade(swiper);
        }}
      >
        {slides}
        <div className={styles.buttonsContainer}>
          <button
            type="button"
            className={cn(styles.swipeButtPrev, {
              [styles.swipeButtHide]: !fadeLeft,
            })}
            onClick={() => recommendationSwiper?.slidePrev()}
          >
            <span className={styles.screenReaderOnly}>
              {t('carousel.prev')}
            </span>
          </button>
          <button
            type="button"
            className={cn(styles.swipeButtNext, {
              [styles.swipeButtHide]: !fadeRight,
            })}
            onClick={() => recommendationSwiper?.slideNext()}
          >
            <span className={styles.screenReaderOnly}>
              {t('carousel.next')}
            </span>
          </button>
        </div>
      </Swiper>
      <a
        href={`/${locale}/${locale === 'fr' ? 'produits' : 'products'}${
          placement?.url
        }`}
        className={styles.footerLink}
      >
        {placement?.linkText || t('product.pdp.collection.shopAll')}
        <IconWrapper
          icon="arrow-right"
          width={18}
          height={16}
          classNames={styles.arrowIcon}
        />
      </a>
    </section>
  );
};

export default ProductCollectionComponent;
